import type { UserMenuEntry } from "@/config/userMenu";
import type { LicenseModule } from "./moduleConfig";
import type { ModifierType } from "./userModifierEnum";

type IsModuleActive = (module: LicenseModule) => boolean;
type IsModifierSet = (modifier: ModifierType) => boolean;

function hasAccess(
  rights: readonly string[],
  requiredRight: UserMenuEntry["accessRight"],
  some = false,
) {
  if (requiredRight == null) return true;
  if (typeof requiredRight === "string") return rights.includes(requiredRight);
  return some
    ? requiredRight.some((right) => rights.includes(right))
    : requiredRight.every((right) => rights.includes(right));
}

function filterMenuEntry(
  entry: UserMenuEntry,
  rights: readonly string[],
  anonym: boolean,
  isModifierSet: IsModifierSet,
  isModuleActive: IsModuleActive,
): UserMenuEntry | undefined {
  if (entry.onlyOnModifier != null && !isModifierSet(entry.onlyOnModifier)) return undefined;
  if (entry.onlyOnModule != null && !isModuleActive(entry.onlyOnModule)) return undefined;

  const canOpenEntry = hasAccess(rights, entry.accessRight, entry.accessRightSome);
  const filteredChildren = entry.child
    ?.map((child) => filterMenuEntry(child, rights, anonym, isModifierSet, isModuleActive))
    .filter((child): child is UserMenuEntry => child != null);
  const href = canOpenEntry && !(entry.hideOnAnonym && anonym) ? entry.href : undefined;

  if (href == null && (filteredChildren == null || filteredChildren.length === 0)) return undefined;

  const visibleEntry = { ...entry, href, child: filteredChildren };
  delete visibleEntry.accessRight;
  delete visibleEntry.accessRightSome;
  delete visibleEntry.hideOnAnonym;
  return visibleEntry;
}

export function filterUserMenu(
  entries: readonly UserMenuEntry[],
  rights: readonly string[],
  anonym: boolean,
  isModifierSet: IsModifierSet = () => true,
  isModuleActive: IsModuleActive = () => true,
) {
  return entries
    .map((entry) => filterMenuEntry(entry, rights, anonym, isModifierSet, isModuleActive))
    .filter((entry): entry is UserMenuEntry => entry != null);
}
